import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import TreemapChart from "../charts/TreemapChart";
import Cards from "../charts/Cards";
import Sectorsplit from "../charts/Sectorsplit";
import CustomTable from "../charts/CustomTable";
import PeriodSelector from "../components/PeriodSelector";
import GET_FUND_HOLDINGS from "../graphql/get_fund_holdings";
import { getHoldingPeriod } from "../utils/dateUtils";

const FundDetails = () => {
  const { id } = useParams();
  console.log("Mutual Fund ID:", id);
  const mutualFundId = parseInt(id);
  
  // Get default holding period based on current date
  const defaultPeriod = getHoldingPeriod();
  const [holdingMonth, setHoldingMonth] = useState(defaultPeriod.holdingMonth);
  const [holdingYear, setHoldingYear] = useState(defaultPeriod.holdingYear);
  const [formattedData, setFormattedData] = useState([]);
  const [sectorAllocation, setSectorAllocation] = useState([]);

  const { loading, error, data, refetch } = useQuery(GET_FUND_HOLDINGS, {
    variables: {
      mutualFundId,
      holdingMonth,
      holdingYear,
    },
    onError: (e) => console.log("Apollo Client Error:", e),
  });

  console.log("Query variables:", {
    mutualFundId,
    holdingMonth,
    holdingYear,
  });

  // Top Gainers and Top Losers based on % change
  const topInclusions = formattedData
    .filter((item) => (item.oneMonthChangeInPtHoldings || 0) > 0)
    .sort((a, b) => (b.oneMonthChangeInPtHoldings || 0) - (a.oneMonthChangeInPtHoldings || 0))
    .slice(0, 5);

  const topExclusions = formattedData
    .filter((item) => (item.oneMonthChangeInPtHoldings || 0) < 0)
    .sort((a, b) => (a.oneMonthChangeInPtHoldings || 0) - (b.oneMonthChangeInPtHoldings || 0))
    .slice(0, 5);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Refetch data when period changes
  useEffect(() => {
    if (mutualFundId && holdingMonth && holdingYear) {
      refetch({
        mutualFundId,
        holdingMonth,
        holdingYear,
      });
    }
  }, [holdingMonth, holdingYear, mutualFundId, refetch]);

  // Format data when fetched
  useEffect(() => {
    if (data && data.getFundHoldings) {
      console.log("Fetched data:", data.getFundHoldings);
      const transformedData = data.getFundHoldings.map((item) => ({
        id: item.id,
        stockName: item.stockName,
        stockSector: item.stockSector,
        holdingMonth: item.holdingMonth,
        holdingYear: item.holdingYear,
        valueInMn: item.valueInMn,
        percentageOfTotalHoldings: item.percentageOfTotalHoldings,
        oneMonthChangeInPtHoldings: item.oneMonthChangeInPtHoldings,
      }));
      setFormattedData(transformedData);

      // Aggregate sector allocation
      const sectorAllocations = transformedData.reduce((acc, item) => {
        if (acc[item.stockSector]) {
          acc[item.stockSector] += item.percentageOfTotalHoldings || 0;
        } else {
          acc[item.stockSector] = item.percentageOfTotalHoldings || 0;
        }
        return acc;
      }, {});

      const sectorData = Object.entries(sectorAllocations).map(
        ([sector, percentage]) => ({
          sector,
          percentage: parseFloat(percentage.toFixed(2)),
        })
      );
      setSectorAllocation(sectorData);
    }
  }, [data]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <>
      {/* Period Selector */}
      <div
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          margin: "20px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
            Fund Holdings
          </h2>
          <p style={{ margin: "5px 0 0 0", color: "#666", fontSize: "14px" }}>
            View portfolio holdings for different periods
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            Select Period:
          </span>
          <PeriodSelector
            holdingMonth={holdingMonth}
            holdingYear={holdingYear}
            onMonthChange={setHoldingMonth}
            onYearChange={setHoldingYear}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "stretch",
          gap: "10px",
        }}
      >
        <Cards title="Top Inclusions" data={topInclusions} />
        <Cards title="Top Exclusions" data={topExclusions} />
        <Sectorsplit data={sectorAllocation} />
      </div>

      {/* Treemap Chart */}
      <div style={{ padding: "20px" }}>
        <TreemapChart data={formattedData} />
      </div>

      {/* Holdings Table */}
      <div style={{ padding: "20px" }}>
        <CustomTable data={formattedData} />
      </div>
    </>
  );
};

export default FundDetails;
